/**
 * GameServices — the only way the ported game talks to the outside world's backend
 * (docs/architecture.md §6, docs/backend.md "Wiring the game").
 *
 * High-level async methods mirror the points where the original game used its VK/server command chain
 * (`main_execute`, FWP.as:14309-14817): profile load/save, server time, tops, assault zones, replays, plus the
 * new multiplayer operations. The facade tracks the profile revision so the game can keep calling
 * `saveProfile(state)` exactly where the original wrote `save storage`.
 *
 * Accounts of the game: `restoreSession()` at start (the credentials stored for this backend origin; null = show the
 * sign-in screen, `restoreFailure` says why), `signUp` / `signIn` (nickname + password; the password goes from the
 * caller's input field into that one request and is kept nowhere), `signOut()` (auth.logout, then this origin's
 * stored credentials and every cached profile value are wiped), `deleteAccount(password?)` (account.delete, then the
 * same wipe), `getMyProfile()`. Guest accounts are never created
 * silently: `ensureSession` signs in as a guest only when the facade was created with `guest: true` (headless tools,
 * tests, the server's battle simulator) or, for the in-process backends only, the page asks with `?autoguest=1`
 * (config.ts autoGuestRequested).
 */
import { nicknameKey } from '../shared/auth-rules';
import { emailKey } from '../shared/email-rules';
import type { JsonObject, JsonValue } from '../shared/json';
import { BackendError } from '../shared/protocol/errors';
import { LIMITS } from '../shared/protocol/validators';
import { type EventMap, type EventType, type HelloResult, type PayloadOf, REQUEST_META, type RequestType, type ResultOf } from '../shared/protocol/messages';
import type {
  AccountDeletedResult,
  AssaultTarget,
  AssaultZone,
  AuctionBuyCommandResult,
  AuctionInfo,
  AuthSessionInfo,
  BattleFinishCommandArgs,
  BattleFinishCommandResult,
  BattleForfeitCommandResult,
  BattleInit,
  BattleMode,
  BattleResult,
  BattleStartCommandArgs,
  BattleStartCommandResult,
  ChatChannelInfo,
  ChatLimits,
  ChatMessage,
  ClanEvent,
  ClanInfo,
  ClanJoinRequest,
  ClanMember,
  ClanMineResult,
  ClanRole,
  CommandVolatile,
  ClanSummary,
  DeviceInfo,
  EmailStatusResult,
  Grant,
  GraveSearchCommandResult,
  LeaderboardBoard,
  LeaderboardPage,
  MatchTicket,
  MessageLanguage,
  NewsItem,
  PlayerCard,
  PlayerId,
  PresentClaimCommandResult,
  SignedInResult,
  ProfileSummary,
  ProfileVolatile,
  PublicProfile,
  ReplayRecord,
  ReplaySummary,
  Room,
  RoomSettings,
  SaveReason,
  SessionInfo,
  SessionOutcome,
  TeamSnapshotInput,
  TributeClaimCommandResult,
  TributeResult,
} from '../shared/protocol/model';
import type { CommandLogData } from '../session/command-log';
import { type StateHasher, canonicalJson } from '../session/hash';
import { LockstepCoordinator } from '../session/lockstep';
import { type BackendConnectionConfig, autoGuestRequested, backendOrigin, battleSeedRequested } from './config';
import { BackendClient, NoReplyError, type RestoreFailureReason, authFailureReason, restoreFailureOf } from './net/client';
import { type CredentialStore, MemoryCredentialStore, WebStorageCredentialStore, credentialsKey } from './net/credentials';
import { HttpTransport } from './net/http';
import { InProcessTransport, createInProcessBackend } from './net/in-process';
import { createLockstepLink } from './net/lockstep-link';
import type { Transport, TransportKind, TransportStatus } from './net/transport';
import type { WebStorageLike } from '../server/core/storage-local';
import { WebSocketTransport } from './net/websocket';

export interface Identity {
  playerId: PlayerId;
  displayName: string;
  username: string | null;
}

export interface LoadedProfile {
  /** The saved game `player` object, or null for a new player (the game then runs create_new_params). */
  gameState: JsonValue | null;
  revision: number;
  created: boolean;
  displayName: string;
  /** Legacy (v45) save waiting to be decoded by the game's read_storage; save with reason 'import' afterwards. */
  legacySave: string | null;
  pendingGrants: number;
  /** Server time, unix seconds (the original `user.unix_time`). */
  serverTime: number;
  /** Facts of the server's session entry (L2: Papa John, the daily present, the calendar); null otherwise. */
  volatile: ProfileVolatile | null;
}

export interface LoadProfileOptions {
  /**
   * The device's time zone, minutes east of UTC (default: the browser's `-new Date().getTimezoneOffset()`, 0 outside a
   * browser). The server keeps the first one and accepts a change at most once per 24 hours.
   */
  tzOffsetMin?: number;
  /** Ask for the developer test profile when the server creates the profile (only where the backend allows it). */
  dev?: boolean;
}

/** Result of `GameServices.command`: the canonical state to adopt as a whole, and the op's result. */
export interface CommandOutcome {
  gameState: JsonValue;
  /** The op's result (the values the screens show, e.g. a roulette prize). */
  result: JsonValue;
  revision: number;
  /** Server time, unix seconds (the original `user.unix_time`). */
  serverTime: number;
  volatile?: CommandVolatile;
}

/** A command outcome whose `result` has the op's documented shape (src/shared/protocol/model.ts). */
export interface TypedCommandOutcome<R> extends Omit<CommandOutcome, 'result'> {
  result: R;
}

/** Per-request timeout of battle.finish: the server may re-simulate the whole battle before it answers. */
export const BATTLE_FINISH_TIMEOUT_MS = 90_000;

export interface SaveOptions {
  reason?: SaveReason;
  /** Summary values the backend cannot derive from the state (e.g. `level` if exp is not stored). */
  summary?: Partial<ProfileSummary>;
  team?: TeamSnapshotInput;
  /** Grants applied to this state (acknowledged atomically with the save). */
  consumedGrants?: string[];
}

/**
 * Connection to the backend as the game shows it: 'open', 'reconnecting' (the transport lost the server and is
 * trying again; requests fail with UNAVAILABLE meanwhile) or 'closed' (disposed).
 */
export type ConnectionStatus = 'open' | 'reconnecting' | 'closed';

export interface RequestOptions {
  /** Per-request timeout (default: the transport's, 15 s); long server-side work such as a battle validation needs more. */
  timeoutMs?: number;
}

/** Thrown by saveProfile when another session saved first (replaces the original "one window" check). */
export class ProfileConflictError extends BackendError {
  constructor(readonly currentRevision: number) {
    super('CONFLICT', `Profile was changed elsewhere (revision ${currentRevision})`, { currentRevision });
    this.name = 'ProfileConflictError';
  }
}

export interface GameServices {
  readonly transportKind: TransportKind;
  readonly client: BackendClient;

  // --- accounts of the game (nickname + password)
  /**
   * True when `ensureSession` may sign in as a guest (the facade was created with `guest: true`, or `?autoguest=1` for
   * an in-process backend). The web game otherwise shows its sign-in screen when `restoreSession` finds no session.
   */
  readonly guestSignIn: boolean;
  /** Why the last `restoreSession` returned null (null after a successful one or before the first call). */
  readonly restoreFailure: RestoreFailureReason | null;
  /**
   * An app start: the session stored for this backend origin (auth.resume / auth.refresh, then auth.session). Null when
   * there is none or the backend refused it (the refused credentials are wiped; `restoreFailure`: 'none' | 'expired' |
   * 'revoked' | 'replaced' | 'invalid'). Never creates a guest. Throws when the backend cannot answer (the caller retries) or the
   * account is banned.
   */
  restoreSession(): Promise<PlayerCard | null>;
  /**
   * auth.signUp: a new account, signed in at once. Refusals are BackendError with `details` { reason, field }
   * (VALIDATION_FAILED nickname_* / password_*, CONFLICT nickname_taken); the rules for instant feedback:
   * src/shared/auth-rules.ts. The password is used for this request only; the caller clears its input field.
   */
  signUp(nickname: string, password: string): Promise<PlayerCard>;
  /** auth.signIn (UNAUTHORIZED 'invalid_credentials', RATE_LIMITED 'too_many_attempts'); see signUp for the password. */
  signIn(nickname: string, password: string): Promise<PlayerCard>;
  /** Signs in to the account linked to a Google Play Games player (auth.playGames with a server auth code). */
  signInWithPlayGames(serverAuthCode: string): Promise<PlayerCard>;

  // --- e-mail sign-in (feature 'email': the server mails links; src/server/core/services/email.ts)
  /** The server offers e-mail sign-in (system.hello features has 'email'). */
  emailAvailable(): boolean;
  /**
   * auth.emailSignUp: a nickname account (the refusals of signUp, plus VALIDATION_FAILED email_* with field 'email')
   * whose address waits for its confirmation link (`lang`: the mail's language).
   */
  signUpWithEmail(nickname: string, email: string, password: string, lang?: MessageLanguage): Promise<PlayerCard>;
  /** auth.emailSignIn with a confirmed address (UNAUTHORIZED 'invalid_credentials' otherwise). */
  signInWithEmail(email: string, password: string): Promise<PlayerCard>;
  /** auth.passwordResetRequest: always resolves (no answer tells whether the address has an account). */
  requestPasswordReset(email: string, lang?: MessageLanguage): Promise<void>;
  /** auth.passwordReset with the token of a reset link; every session of the account ends. */
  resetPassword(token: string, password: string): Promise<void>;
  /** auth.emailVerify with the token of a confirmation link: the confirmed address, partly hidden. */
  verifyEmail(token: string): Promise<string>;
  /** auth.emailStatus: the account's sign-in address and the pending one. */
  emailStatus(): Promise<EmailStatusResult>;
  /** auth.emailChange (confirmed with the account's password; wrong: UNAUTHORIZED 'invalid_credentials', the session stays). */
  changeEmail(email: string, password: string, lang?: MessageLanguage): Promise<EmailStatusResult>;
  /** auth.emailResend: the pending address's link again. */
  resendEmail(lang?: MessageLanguage): Promise<EmailStatusResult>;
  /** auth.logout (best effort), then this origin's stored credentials and every cached profile value are wiped. */
  signOut(): Promise<void>;
  /**
   * account.delete: deletes the signed-in player's account and all of its data for good. An account with a password
   * confirms with it (the caller's input field goes into this one request and is kept nowhere; wrong: UNAUTHORIZED
   * 'invalid_credentials', the session stays); a guest passes nothing. On success every credential stored for this
   * origin and every cached profile value are wiped.
   */
  deleteAccount(password?: string): Promise<AccountDeletedResult>;
  /** profile.me: the signed-in player's card (nickname, level and experience of the canonical state). */
  getMyProfile(): Promise<PlayerCard>;

  // --- identity
  /**
   * The current session, restored from the stored credentials when needed. Without one it signs in as a guest only
   * when `guest` (default: `guestSignIn`) allows it; otherwise it fails with UNAUTHORIZED 'session_required'.
   */
  ensureSession(opts?: { displayName?: string; guest?: boolean; takeOver?: boolean }): Promise<Identity>;
  register(username: string, password: string, displayName?: string): Promise<Identity>;
  login(username: string, password: string): Promise<Identity>;
  linkCredentials(username: string, password: string): Promise<void>;
  /** Ends this device session (`all`: every session of the account). */
  logout(opts?: { all?: boolean }): Promise<void>;
  /** The account's device sessions (platform, app version, created / last seen), most recently used first. */
  listSessions(): Promise<AuthSessionInfo[]>;
  /** Ends one device session of the account (its connections are closed); the own one = logout. */
  revokeSession(sessionId: string): Promise<void>;
  getIdentity(): Identity | null;
  setDisplayName(name: string): Promise<void>;

  // --- time
  /** Server time in unix seconds (replaces `get time` / `refresh time`). */
  getServerTime(): Promise<number>;

  // --- profile
  loadProfile(opts?: LoadProfileOptions): Promise<LoadedProfile>;
  saveProfile(gameState: JsonValue, opts?: SaveOptions): Promise<{ revision: number; summary: ProfileSummary }>;
  importLegacySave(legacy: string): Promise<{ revision: number }>;
  getPendingGrants(): Promise<Grant[]>;
  getPublicProfile(playerId: PlayerId): Promise<PublicProfile>;
  getPublicProfiles(playerIds: PlayerId[]): Promise<PublicProfile[]>;

  // --- commands (the server owns the game state)
  /**
   * Applies a meta-game operation (`op`, e.g. 'shop.buy', with its `args`) on the server and returns the new canonical
   * state. One logical command has one opId: a call repeated with the same op and args while the outcome of the
   * previous one is unknown (no reply, or an INTERNAL / UNAVAILABLE / RATE_LIMITED / UNAUTHORIZED error reply) reuses
   * it, so the server answers from its journal and never applies the op twice; after a success or a definite
   * rejection (e.g. VALIDATION_FAILED) the same call is a new command. `timeoutMs`: long server-side work (battles).
   */
  command(op: string, args?: JsonObject, opts?: RequestOptions): Promise<CommandOutcome>;

  // --- solo battles and the claims whose inputs the server owns (typed `command` calls, same opId retry rules)
  /** battle.start: the server's pending battle, its seed and everything the game starts it with. */
  startSoloBattle(args: BattleStartCommandArgs): Promise<TypedCommandOutcome<BattleStartCommandResult>>;
  /** battle.finish: the claim and the command log; the server may re-simulate it (timeout BATTLE_FINISH_TIMEOUT_MS). */
  finishSoloBattle(args: BattleFinishCommandArgs, opts?: RequestOptions): Promise<TypedCommandOutcome<BattleFinishCommandResult>>;
  /** battle.forfeit: the pending battle ends as a loss. */
  forfeitSoloBattle(battleId: string): Promise<TypedCommandOutcome<BattleForfeitCommandResult>>;
  /** grave.search: grave `index` (1-based) of the last won battle. */
  searchGrave(index: number): Promise<TypedCommandOutcome<GraveSearchCommandResult>>;
  /** present.claim: today's present, once (the server knows whether one is due: profile.load volatile). */
  claimPresent(): Promise<TypedCommandOutcome<PresentClaimCommandResult>>;
  /** tribute.claim: the tribute of the player's and the clan's zones, credited to the state. */
  collectTribute(): Promise<TypedCommandOutcome<TributeClaimCommandResult>>;

  // --- tops, assault, clans
  getLeaderboard(board: LeaderboardBoard, page?: number): Promise<LeaderboardPage>;
  getAssaultZones(): Promise<{ serverTime: number; zones: AssaultZone[] }>;
  getAssaultTargets(zone?: number): Promise<AssaultTarget[]>;
  reportAssault(input: PayloadOf<'assault.report'>): Promise<ResultOf<'assault.report'>>;
  /** Legacy tribute request of the client-owned state (L0/L1; refused at L2): use `collectTribute`. */
  claimTribute(): Promise<TributeResult>;
  /** The first page of clan.list (older callers; the game uses listClansPage). */
  getClans(): Promise<ClanSummary[]>;
  /** Every member of a clan (all pages of clan.members): the leader, the officers, the members. */
  getClanMembers(clanId: number): Promise<ClanMember[]>;
  /** clan.join: joins an open clan (FORBIDDEN 'clan_request_required' for a clan that takes requests). */
  joinClan(clanId: number): Promise<{ clanId: number; role: ClanRole }>;
  /** clan.leave: leaves the player's clan (the only member leaving disbands it). */
  leaveClan(): Promise<ResultOf<'clan.leave'>>;
  // Player-made clans (src/shared/clan-rules.ts; system.hello limits.clans).
  /** clan.mine: the player's clan and role, its own requests, the clan's pending requests, what it may do. */
  getMyClan(): Promise<ClanMineResult>;
  /** clan.get: a clan's card (summary, description, leader, the zones its members hold). */
  getClan(clanId: number): Promise<ClanInfo>;
  /** One page of clan.list: by rating (default) or the newest first, optionally the names containing `query`. */
  listClansPage(opts?: { sort?: 'rating' | 'new'; query?: string; limit?: number; cursor?: string }): Promise<{ clans: ClanSummary[]; next: string | null }>;
  createClan(input: PayloadOf<'clan.create'>): Promise<ClanInfo>;
  updateClan(input: PayloadOf<'clan.update'>): Promise<ClanInfo>;
  /** clan.request: asks to join a clan whose policy is 'request'. */
  requestClan(clanId: number): Promise<ClanJoinRequest>;
  cancelClanRequest(clanId: number): Promise<void>;
  /** One page of the clan's pending requests, oldest first (leader and officers). */
  listClanRequests(clanId: number, opts?: { limit?: number; cursor?: string }): Promise<{ items: ClanJoinRequest[]; next: string | null }>;
  decideClanRequest(clanId: number, playerId: PlayerId, accept: boolean): Promise<void>;
  kickClanMember(clanId: number, playerId: PlayerId): Promise<void>;
  setClanRole(clanId: number, playerId: PlayerId, role: 'officer' | 'member'): Promise<void>;
  /** clan.transfer: the leadership to another member (the leader becomes an officer). */
  transferClan(clanId: number, playerId: PlayerId): Promise<void>;
  /** clan.disband: `confirmName` repeats the clan's name. */
  disbandClan(clanId: number, confirmName: string): Promise<void>;
  /** One page of a clan's history, newest first. */
  getClanHistoryPage(clanId: number, opts?: { limit?: number; cursor?: string }): Promise<{ items: ClanEvent[]; next: string | null }>;

  // --- chat (the global channel and the clan's; src/shared/chat-rules.ts)
  /** Whether the server offers the chat (feature 'chat'). */
  chatAvailable(): boolean;
  /** system.hello limits.chat (null before the handshake or without the chat). */
  chatLimits(): ChatLimits | null;
  /** chat.channels: the readable channels with their unread counts (`exclude`: the players this device hides). */
  chatChannels(exclude?: PlayerId[]): Promise<{ channels: ChatChannelInfo[]; mutedUntil: number | null }>;
  /** chat.history: one page of a channel, newest first. */
  chatHistory(channel: string, opts?: { limit?: number; cursor?: string }): Promise<{ items: ChatMessage[]; next: string | null }>;
  /** chat.send: the stored message (sanitized and masked by the server). */
  chatSend(channel: string, text: string): Promise<ChatMessage>;
  /** chat.subscribe / chat.unsubscribe: the channels whose chat.message events this player gets now. */
  chatSubscribe(channels: string[]): Promise<string[]>;
  chatUnsubscribe(channels?: string[]): Promise<string[]>;
  /** chat.markRead: the read position moves forward to `seq`. */
  chatMarkRead(channel: string, seq: number): Promise<{ readSeq: number; unread: number }>;

  // --- auction (the Annihilator "king of the hill", paid in diamonds), news, replays
  /** auction.get: price, holder, round end, winners and the player's own Annihilators. */
  getAuction(): Promise<AuctionInfo>;
  /** auction.buy: buys the Annihilator at `expectedPrice` diamonds (another price: VALIDATION_FAILED 'price_changed'). */
  buyAuction(expectedPrice: number): Promise<TypedCommandOutcome<AuctionBuyCommandResult>>;
  getNews(limit?: number): Promise<NewsItem[]>;
  saveReplay(replay: PayloadOf<'replay.save'>): Promise<string>;
  listReplays(playerId?: PlayerId, limit?: number): Promise<ReplaySummary[]>;
  getReplay(replayId: string): Promise<ReplayRecord>;

  // --- battles
  /** Starts a backend-tracked battle and returns the backend-issued seed (`cur_random`, 0..399). */
  startBattle(init: BattleInit): Promise<SessionInfo>;
  finishBattle(
    sessionId: string,
    result: BattleResult,
    opts?: { finalHash?: string; log?: CommandLogData; timeoutMs?: number },
  ): Promise<{ ended: boolean; outcome: SessionOutcome | null }>;
  forfeitBattle(sessionId: string): Promise<void>;
  /** This client's simulation disagrees with the online battle in `tick`: the server decides (session.dispute). */
  disputeBattle(sessionId: string, tick: number, reason?: string): Promise<ResultOf<'session.dispute'>>;

  // --- online multiplayer
  findMatch(opts: { teams: number; wormsPerTeam: number }): Promise<MatchTicket>;
  cancelMatch(ticketId: string): Promise<void>;
  listRooms(): Promise<Room[]>;
  /** One page of the public lobby rooms (room.list): `cursor` = the `next` of the previous page (null: the last one). */
  listRoomsPage(opts?: { cursor?: string; limit?: number }): Promise<{ rooms: Room[]; next: string | null }>;
  /** room.get: a room this player can see (a private one only as a member). */
  getRoom(roomId: string): Promise<Room>;
  createRoom(opts?: { settings?: RoomSettings; private?: boolean; maxPlayers?: number }): Promise<Room>;
  joinRoom(target: { roomId?: string; code?: string }): Promise<Room>;
  leaveRoom(roomId: string): Promise<void>;
  setReady(roomId: string, ready: boolean): Promise<Room>;
  setTeam(roomId: string, team: number): Promise<Room>;
  updateRoomSettings(roomId: string, settings: RoomSettings): Promise<Room>;
  sendChat(roomId: string, text: string): Promise<void>;
  startRoom(roomId: string): Promise<SessionInfo>;
  getSession(sessionId: string): Promise<ResultOf<'session.get'>>;
  /** Lockstep coordinator for a multiplayer session; local teams are taken from the session participants. */
  createLockstep(session: SessionInfo, opts?: { hasher?: StateHasher; flushEveryTicks?: number }): LockstepCoordinator;
  /** Coordinator for a session already in progress (after a reload/reconnect), seeded with the backend's log. */
  resumeLockstep(sessionId: string, opts?: { hasher?: StateHasher; flushEveryTicks?: number }): Promise<LockstepCoordinator>;

  // --- generic requests
  /**
   * Sends any protocol request. Mutating requests are idempotent across retries: a request with the same type and
   * payload as an earlier one whose outcome is unknown (no reply: a transport failure or a timeout, or still in
   * flight) is sent under the same request id, so the backend answers it from its idempotency cache instead of
   * applying it twice. A reply of the backend is a known outcome, errors included (UNAVAILABLE, RATE_LIMITED): the
   * backend caches the replies of mutations, so resending that id would only repeat the error; a retry is new.
   */
  request<T extends RequestType>(type: T, payload: PayloadOf<T>, opts?: RequestOptions): Promise<ResultOf<T>>;

  // --- events and connection
  on<E extends EventType>(event: E, handler: (payload: EventMap[E]) => void): () => void;
  /**
   * Server events were missed and cannot be replayed (after a reconnect: the server's replay buffer overflowed, or it
   * restarted): reload the state the events keep current (the game reloads the profile).
   */
  onEventGap(listener: () => void): () => void;
  /**
   * True after the account signed in (or resumed another session) on another device, which ended this session (one
   * active session per account), until the next sign-in here. Nothing signs in again by itself meanwhile:
   * `ensureSession` fails with UNAUTHORIZED 'session_replaced' unless `takeOver` (the player asked to play here).
   */
  readonly sessionReplaced: boolean;
  /** Called when that happens (the WebSocket closed with 4004, or a request answered 'session_replaced'). */
  onSessionReplaced(listener: () => void): () => void;
  /** The server's system.hello answer (protocol, features, limits, public URL); null before the handshake. */
  readonly serverInfo: HelloResult | null;
  readonly connectionStatus: ConnectionStatus;
  onConnectionStatus(listener: (status: ConnectionStatus) => void): () => void;
  dispose(): Promise<void>;
}

/**
 * A failed mutation that may still have been applied: no reply arrived (the request id is kept for the retry). An error
 * reply, even UNAVAILABLE or RATE_LIMITED, is the backend's answer (it was not applied); the retry gets a new id.
 */
function outcomeUnknown(err: unknown): boolean {
  return err instanceof NoReplyError;
}

/**
 * Errors after which a command keeps its opId for the retry: no reply, or a reply that says nothing about the command
 * itself (the backend journals only commands it ran to an answer, so the retry is applied at most once).
 */
const COMMAND_RETRY_CODES: ReadonlySet<string> = new Set(['UNAVAILABLE', 'INTERNAL', 'RATE_LIMITED', 'UNAUTHORIZED']);

function keepOpId(err: unknown): boolean {
  return err instanceof NoReplyError || (err instanceof BackendError && COMMAND_RETRY_CODES.has(err.code));
}

/** The device's time zone for profile.load: minutes east of UTC in a browser, 0 elsewhere (Node hosts, tests). */
function deviceTzOffsetMin(): number {
  if (typeof document === 'undefined') return 0;
  const tz = -new Date().getTimezoneOffset() || 0;
  return Number.isInteger(tz) && tz >= LIMITS.tzOffsetMinMin && tz <= LIMITS.tzOffsetMinMax ? tz : 0;
}

/** Oldest-first bound of the remembered unconfirmed mutations. */
const MAX_UNCONFIRMED = 64;

/** Most pages a facade method fetches to return a whole list (e.g. all clan members). */
const MAX_LIST_PAGES = 50;

class GameServicesImpl implements GameServices {
  private revision = 0;
  private identity: Identity | null = null;
  /** Mutations whose outcome is not known yet, by type + canonical payload (see `request`). */
  private readonly unconfirmed = new Map<string, { id: string; inflight: Promise<unknown> | null }>();
  /** opIds of commands whose outcome is not known yet, by op + canonical args (see `command`). */
  private readonly pendingOps = new Map<string, string>();
  private status: ConnectionStatus = 'open';
  private readonly statusListeners = new Set<(status: ConnectionStatus) => void>();
  private readonly offStatus: () => void;
  private readonly offClanEvents: (() => void)[];
  restoreFailure: RestoreFailureReason | null = null;
  /**
   * Request ids of sign-ups / sign-ins that got no reply, by type and nickname KEY (never the password): the player's
   * retry goes under the same id, so a sign-up the server applied answers with its account instead of 'nickname_taken'
   * (the server's idempotency key includes the payload: another password simply runs as a new request).
   */
  private readonly unansweredSignIns = new Map<string, string>();

  constructor(
    readonly client: BackendClient,
    readonly transportKind: TransportKind,
    readonly guestSignIn = false,
  ) {
    this.offStatus = client.transport.onStatus((s) => this.setStatus(s));
    // A clan event tells what became of the clan actions whose reply was lost: a later action of the same kind and
    // payload (leave again, in another clan) is a new one, not the retry of the old (its cached reply).
    const settleClan = () => {
      for (const [key, entry] of [...this.unconfirmed]) if (key.startsWith('clan.') && !entry.inflight) this.unconfirmed.delete(key);
    };
    this.offClanEvents = (['clan.membership', 'clan.updated', 'clan.requestsChanged'] as const).map((e) => client.on(e, settleClan));
  }

  get connectionStatus(): ConnectionStatus {
    return this.status;
  }

  onConnectionStatus(listener: (status: ConnectionStatus) => void): () => void {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  }

  private setStatus(s: TransportStatus): void {
    const next: ConnectionStatus = s === 'open' ? 'open' : s === 'closed' ? 'closed' : 'reconnecting';
    if (next === this.status || this.status === 'closed') return;
    this.status = next;
    for (const l of [...this.statusListeners]) {
      try {
        l(next);
      } catch {
        /* listener errors are isolated */
      }
    }
  }

  request<T extends RequestType>(type: T, payload: PayloadOf<T>, opts: RequestOptions = {}): Promise<ResultOf<T>> {
    return this.call(type, payload, opts);
  }

  /** client.call with the retry-safe request ids of mutations (see GameServices.request). */
  private call<T extends RequestType>(type: T, payload: PayloadOf<T>, opts: RequestOptions = {}): Promise<ResultOf<T>> {
    const callOpts = opts.timeoutMs ? { timeoutMs: opts.timeoutMs } : {};
    if (!REQUEST_META[type]?.mutating) return this.client.call(type, payload, callOpts);
    const key = `${type}|${canonicalJson(payload as never)}`;
    const known = this.unconfirmed.get(key);
    // A retry of a mutation that is still in flight joins it instead of racing it under a second id.
    if (known?.inflight) return known.inflight as Promise<ResultOf<T>>;
    const entry = known ?? { id: this.client.newRequestId(), inflight: null };
    this.unconfirmed.delete(key);
    this.unconfirmed.set(key, entry);
    while (this.unconfirmed.size > MAX_UNCONFIRMED) this.unconfirmed.delete(this.unconfirmed.keys().next().value!);
    const p = this.client.call(type, payload, { ...callOpts, id: entry.id }).then(
      (r) => {
        if (this.unconfirmed.get(key) === entry) this.unconfirmed.delete(key);
        return r;
      },
      (err) => {
        entry.inflight = null;
        if (!outcomeUnknown(err) && this.unconfirmed.get(key) === entry) this.unconfirmed.delete(key);
        throw err;
      },
    );
    entry.inflight = p;
    return p;
  }

  private setIdentity(): Identity {
    const s = this.client.currentSession;
    if (!s) throw new BackendError('UNAUTHORIZED', 'No backend session', { reason: 'session_required' });
    this.identity = { playerId: s.playerId, displayName: s.displayName, username: s.username };
    return this.identity;
  }

  get sessionReplaced(): boolean {
    return this.client.sessionReplaced;
  }

  onSessionReplaced(listener: () => void): () => void {
    return this.client.onSessionReplaced(listener);
  }

  async ensureSession(opts: { displayName?: string; guest?: boolean; takeOver?: boolean } = {}): Promise<Identity> {
    await this.client.ensureSession(opts.displayName, { guest: opts.guest ?? this.guestSignIn, takeOver: opts.takeOver });
    return this.setIdentity();
  }

  /** Forgets everything cached about the signed-in player (another player may sign in next). */
  private resetCached(): void {
    this.identity = null;
    this.revision = 0;
    this.unconfirmed.clear();
    this.pendingOps.clear();
  }

  /** The identity of a player card just received (the session is the client's). */
  private adoptCard(card: PlayerCard): PlayerCard {
    this.setIdentity();
    return card;
  }

  async restoreSession(): Promise<PlayerCard | null> {
    const had = this.client.currentSession?.playerId ?? null;
    const r = await this.client.restoreSession();
    if (!r.restored) {
      this.resetCached();
      this.restoreFailure = r.reason;
      return null;
    }
    try {
      const { player } = await this.client.call('auth.session', {});
      if (had !== player.playerId) this.resetCached();
      this.restoreFailure = null;
      return this.adoptCard(player);
    } catch (err) {
      const reason = authFailureReason(err as BackendError);
      if (err instanceof NoReplyError || reason === null) throw err;
      // Ended between the restore and this request: the same as a refused restore.
      this.client.forgetStoredSession();
      this.resetCached();
      this.restoreFailure = restoreFailureOf(reason);
      return null;
    }
  }

  async signUp(nickname: string, password: string): Promise<PlayerCard> {
    return this.signInWith('auth.signUp', nickname, password);
  }

  async signIn(nickname: string, password: string): Promise<PlayerCard> {
    return this.signInWith('auth.signIn', nickname, password);
  }

  private async signInWith(type: 'auth.signUp' | 'auth.signIn', nickname: string, password: string): Promise<PlayerCard> {
    const key = `${type}|${nicknameKey(String(nickname))}`;
    const id = this.unansweredSignIns.get(key) ?? this.client.newRequestId();
    this.unansweredSignIns.set(key, id);
    let r: SignedInResult;
    try {
      r = type === 'auth.signUp' ? await this.client.signUp(nickname, password, { id }) : await this.client.signIn(nickname, password, { id });
    } catch (err) {
      if (!outcomeUnknown(err)) this.unansweredSignIns.delete(key);
      throw err;
    }
    this.unansweredSignIns.delete(key);
    this.resetCached();
    this.restoreFailure = null;
    return this.adoptCard(r.player);
  }

  emailAvailable(): boolean {
    return !!this.serverInfo && this.serverInfo.features.includes('email');
  }

  async signUpWithEmail(nickname: string, email: string, password: string, lang?: MessageLanguage): Promise<PlayerCard> {
    return this.signInOnce(`auth.emailSignUp|${nicknameKey(String(nickname))}|${emailKey(String(email))}`, (id) => this.client.signUpWithEmail(nickname, email, password, lang, { id }));
  }

  async signInWithEmail(email: string, password: string): Promise<PlayerCard> {
    return this.signInOnce(`auth.emailSignIn|${emailKey(String(email))}`, (id) => this.client.signInWithEmail(email, password, { id }));
  }

  async requestPasswordReset(email: string, lang?: MessageLanguage): Promise<void> {
    await this.call('auth.passwordResetRequest', { email, ...(lang ? { lang } : {}) });
  }

  async resetPassword(token: string, password: string): Promise<void> {
    // Straight to the client: the password is part of this one request only (no retry key keeps it).
    await this.client.call('auth.passwordReset', { token, password });
  }

  async verifyEmail(token: string): Promise<string> {
    return (await this.call('auth.emailVerify', { token })).email;
  }

  emailStatus(): Promise<EmailStatusResult> {
    return this.call('auth.emailStatus', {});
  }

  changeEmail(email: string, password: string, lang?: MessageLanguage): Promise<EmailStatusResult> {
    return this.client.call('auth.emailChange', { email, password, ...(lang ? { lang } : {}) });
  }

  resendEmail(lang?: MessageLanguage): Promise<EmailStatusResult> {
    return this.call('auth.emailResend', lang ? { lang } : {});
  }

  /** A sign-in under one request id until it is answered (a retry of a lost reply gets the same session). */
  private async signInOnce(key: string, send: (id: string) => Promise<SignedInResult>): Promise<PlayerCard> {
    const id = this.unansweredSignIns.get(key) ?? this.client.newRequestId();
    this.unansweredSignIns.set(key, id);
    let r: SignedInResult;
    try {
      r = await send(id);
    } catch (err) {
      if (!outcomeUnknown(err)) this.unansweredSignIns.delete(key);
      throw err;
    }
    this.unansweredSignIns.delete(key);
    this.resetCached();
    this.restoreFailure = null;
    return this.adoptCard(r.player);
  }

  async signInWithPlayGames(serverAuthCode: string): Promise<PlayerCard> {
    const r = await this.client.signInWithPlayGames(serverAuthCode);
    this.resetCached();
    this.restoreFailure = null;
    return this.adoptCard(r.player);
  }

  async signOut(): Promise<void> {
    try {
      await this.client.signOut();
    } finally {
      this.resetCached();
      this.unansweredSignIns.clear();
    }
  }

  async deleteAccount(password?: string): Promise<AccountDeletedResult> {
    const result = await this.client.deleteAccount(password);
    this.resetCached();
    this.unansweredSignIns.clear();
    this.restoreFailure = null;
    return result;
  }

  getMyProfile(): Promise<PlayerCard> {
    return this.call('profile.me', {});
  }

  async register(username: string, password: string, displayName?: string): Promise<Identity> {
    await this.client.call('auth.register', displayName ? { username, password, displayName } : { username, password });
    this.revision = 0;
    return this.setIdentity();
  }

  async login(username: string, password: string): Promise<Identity> {
    await this.client.call('auth.login', { username, password });
    this.revision = 0;
    return this.setIdentity();
  }

  async linkCredentials(username: string, password: string): Promise<void> {
    const r = await this.client.call('auth.link', { username, password });
    if (this.identity) this.identity.username = r.username;
  }

  async logout(opts: { all?: boolean } = {}): Promise<void> {
    await this.client.call('auth.logout', opts.all ? { all: true } : {});
    this.identity = null;
    this.revision = 0;
  }

  async listSessions(): Promise<AuthSessionInfo[]> {
    return (await this.client.call('auth.sessions', {})).sessions;
  }

  async revokeSession(sessionId: string): Promise<void> {
    const own = this.client.currentSession?.sessionId === sessionId;
    await this.client.call('auth.revoke', { sessionId });
    if (own) {
      this.identity = null;
      this.revision = 0;
    }
  }

  getIdentity(): Identity | null {
    return this.identity;
  }

  async setDisplayName(name: string): Promise<void> {
    await this.call('profile.setDisplayName', { displayName: name });
    if (this.identity) this.identity.displayName = name;
  }

  async getServerTime(): Promise<number> {
    const r = await this.call('system.time', {});
    return Math.floor(r.serverTime / 1000);
  }

  async loadProfile(opts: LoadProfileOptions = {}): Promise<LoadedProfile> {
    const payload: PayloadOf<'profile.load'> = { tzOffsetMin: opts.tzOffsetMin ?? deviceTzOffsetMin() };
    if (opts.dev) payload.dev = true;
    const r = await this.call('profile.load', payload);
    this.revision = r.profile.revision;
    return {
      gameState: r.profile.gameState,
      revision: r.profile.revision,
      created: r.created,
      displayName: r.profile.displayName,
      legacySave: r.profile.legacyImport?.data ?? null,
      pendingGrants: r.pendingGrants,
      serverTime: Math.floor(r.serverTime / 1000),
      volatile: r.volatile ?? null,
    };
  }

  async command(op: string, args: JsonObject = {}, opts: RequestOptions = {}): Promise<CommandOutcome> {
    const key = `${op}|${canonicalJson(args as never)}`;
    const opId = this.pendingOps.get(key) ?? this.client.newRequestId();
    this.pendingOps.delete(key);
    this.pendingOps.set(key, opId);
    while (this.pendingOps.size > MAX_UNCONFIRMED) this.pendingOps.delete(this.pendingOps.keys().next().value!);
    let r: ResultOf<'player.command'>;
    try {
      r = await this.call('player.command', { op, args, opId }, opts);
    } catch (err) {
      if (!keepOpId(err) && this.pendingOps.get(key) === opId) this.pendingOps.delete(key);
      throw err;
    }
    if (this.pendingOps.get(key) === opId) this.pendingOps.delete(key);
    this.revision = r.revision;
    const out: CommandOutcome = { gameState: r.gameState, result: r.result, revision: r.revision, serverTime: Math.floor(r.serverTime / 1000) };
    if (r.volatile) out.volatile = r.volatile;
    return out;
  }

  private async typedCommand<R>(op: string, args: object, opts: RequestOptions = {}): Promise<TypedCommandOutcome<R>> {
    return (await this.command(op, args as JsonObject, opts)) as unknown as TypedCommandOutcome<R>;
  }

  startSoloBattle(args: BattleStartCommandArgs): Promise<TypedCommandOutcome<BattleStartCommandResult>> {
    return this.typedCommand('battle.start', args);
  }

  finishSoloBattle(args: BattleFinishCommandArgs, opts: RequestOptions = {}): Promise<TypedCommandOutcome<BattleFinishCommandResult>> {
    return this.typedCommand('battle.finish', args, { timeoutMs: opts.timeoutMs ?? BATTLE_FINISH_TIMEOUT_MS });
  }

  forfeitSoloBattle(battleId: string): Promise<TypedCommandOutcome<BattleForfeitCommandResult>> {
    return this.typedCommand('battle.forfeit', { battleId });
  }

  searchGrave(index: number): Promise<TypedCommandOutcome<GraveSearchCommandResult>> {
    return this.typedCommand('grave.search', { index });
  }

  claimPresent(): Promise<TypedCommandOutcome<PresentClaimCommandResult>> {
    return this.typedCommand('present.claim', {});
  }

  collectTribute(): Promise<TypedCommandOutcome<TributeClaimCommandResult>> {
    return this.typedCommand('tribute.claim', {});
  }

  async saveProfile(gameState: JsonValue, opts: SaveOptions = {}): Promise<{ revision: number; summary: ProfileSummary }> {
    const payload: PayloadOf<'profile.save'> = { gameState, baseRevision: this.revision };
    if (opts.reason) payload.reason = opts.reason;
    if (opts.summary) payload.summary = opts.summary;
    if (opts.team) payload.team = opts.team;
    if (opts.consumedGrants?.length) payload.consumedGrants = opts.consumedGrants;
    // A save whose outcome is unknown (no reply: the request may have been applied but the reply was lost, or the
    // game's watchdog gave up on it while it was still in flight) is retried by the caller with the same payload; it
    // reuses the request id (see `call`), so the backend's idempotency cache answers it with the original result
    // instead of a revision CONFLICT against our own earlier write.
    try {
      const r = await this.call('profile.save', payload);
      this.revision = r.revision;
      return { revision: r.revision, summary: r.summary };
    } catch (err) {
      if (err instanceof BackendError && err.code === 'CONFLICT') {
        const cur = (err.details as { currentRevision?: number } | undefined)?.currentRevision ?? -1;
        throw new ProfileConflictError(cur);
      }
      throw err;
    }
  }

  async importLegacySave(legacy: string): Promise<{ revision: number }> {
    const r = await this.call('profile.importLegacy', { legacy, baseRevision: this.revision });
    this.revision = r.revision;
    return { revision: r.revision };
  }

  async getPendingGrants(): Promise<Grant[]> {
    return (await this.call('profile.listGrants', {})).grants;
  }

  async getPublicProfile(playerId: PlayerId): Promise<PublicProfile> {
    return (await this.call('profile.getPublic', { playerId })).profile;
  }

  async getPublicProfiles(playerIds: PlayerId[]): Promise<PublicProfile[]> {
    return (await this.call('profile.getPublicBatch', { playerIds })).profiles;
  }

  getLeaderboard(board: LeaderboardBoard, page = 1): Promise<LeaderboardPage> {
    return this.call('leaderboard.get', { board, page });
  }

  getAssaultZones(): Promise<{ serverTime: number; zones: AssaultZone[] }> {
    return this.call('assault.getZones', {});
  }

  async getAssaultTargets(zone?: number): Promise<AssaultTarget[]> {
    return (await this.call('assault.getTargets', zone === undefined ? {} : { zone })).targets;
  }

  reportAssault(input: PayloadOf<'assault.report'>): Promise<ResultOf<'assault.report'>> {
    return this.call('assault.report', input);
  }

  claimTribute(): Promise<TributeResult> {
    return this.call('assault.claimTribute', {});
  }

  async getClans(): Promise<ClanSummary[]> {
    return (await this.call('clan.list', {})).clans;
  }

  async getClanMembers(clanId: number): Promise<ClanMember[]> {
    // Every member (the clan screens count them): all pages of the paginated list.
    const members: ClanMember[] = [];
    let cursor: string | undefined;
    for (let page = 0; page < MAX_LIST_PAGES; page++) {
      const r = await this.call('clan.members', cursor === undefined ? { clanId } : { clanId, cursor });
      members.push(...(r.items ?? r.members));
      if (!r.next) break;
      cursor = r.next;
    }
    return members;
  }

  joinClan(clanId: number): Promise<{ clanId: number; role: ClanRole }> {
    return this.call('clan.join', { clanId });
  }

  leaveClan(): Promise<ResultOf<'clan.leave'>> {
    return this.call('clan.leave', {});
  }

  getMyClan(): Promise<ClanMineResult> {
    return this.call('clan.mine', {});
  }

  async getClan(clanId: number): Promise<ClanInfo> {
    return (await this.call('clan.get', { clanId })).clan;
  }

  async listClansPage(opts: { sort?: 'rating' | 'new'; query?: string; limit?: number; cursor?: string } = {}): Promise<{ clans: ClanSummary[]; next: string | null }> {
    const p: PayloadOf<'clan.list'> = {};
    if (opts.sort !== undefined) p.sort = opts.sort;
    if (opts.query !== undefined) p.query = opts.query;
    if (opts.limit !== undefined) p.limit = opts.limit;
    if (opts.cursor !== undefined) p.cursor = opts.cursor;
    const r = await this.call('clan.list', p);
    return { clans: r.items ?? r.clans, next: r.next ?? null };
  }

  async createClan(input: PayloadOf<'clan.create'>): Promise<ClanInfo> {
    return (await this.call('clan.create', input)).clan;
  }

  async updateClan(input: PayloadOf<'clan.update'>): Promise<ClanInfo> {
    return (await this.call('clan.update', input)).clan;
  }

  async requestClan(clanId: number): Promise<ClanJoinRequest> {
    return (await this.call('clan.request', { clanId })).request;
  }

  async cancelClanRequest(clanId: number): Promise<void> {
    await this.call('clan.cancelRequest', { clanId });
  }

  async listClanRequests(clanId: number, opts: { limit?: number; cursor?: string } = {}): Promise<{ items: ClanJoinRequest[]; next: string | null }> {
    const r = await this.call('clan.requests', { clanId, ...opts });
    return { items: r.items, next: r.next ?? null };
  }

  async decideClanRequest(clanId: number, playerId: PlayerId, accept: boolean): Promise<void> {
    await this.call('clan.decide', { clanId, playerId, accept });
  }

  async kickClanMember(clanId: number, playerId: PlayerId): Promise<void> {
    await this.call('clan.kick', { clanId, playerId });
  }

  async setClanRole(clanId: number, playerId: PlayerId, role: 'officer' | 'member'): Promise<void> {
    await this.call('clan.setRole', { clanId, playerId, role });
  }

  async transferClan(clanId: number, playerId: PlayerId): Promise<void> {
    await this.call('clan.transfer', { clanId, playerId });
  }

  async disbandClan(clanId: number, confirmName: string): Promise<void> {
    await this.call('clan.disband', { clanId, confirmName });
  }

  async getClanHistoryPage(clanId: number, opts: { limit?: number; cursor?: string } = {}): Promise<{ items: ClanEvent[]; next: string | null }> {
    const r = await this.call('clan.history', { clanId, ...opts });
    return { items: r.items, next: r.next ?? null };
  }

  chatAvailable(): boolean {
    return !!this.serverInfo && this.serverInfo.features.includes('chat');
  }

  chatLimits(): ChatLimits | null {
    return this.chatAvailable() ? (this.serverInfo!.limits.chat ?? null) : null;
  }

  chatChannels(exclude: PlayerId[] = []): Promise<{ channels: ChatChannelInfo[]; mutedUntil: number | null }> {
    return this.call('chat.channels', exclude.length > 0 ? { exclude: exclude.slice(0, 200) } : {});
  }

  async chatHistory(channel: string, opts: { limit?: number; cursor?: string } = {}): Promise<{ items: ChatMessage[]; next: string | null }> {
    const r = await this.call('chat.history', { channel, ...opts });
    return { items: r.items, next: r.next ?? null };
  }

  async chatSend(channel: string, text: string): Promise<ChatMessage> {
    return (await this.call('chat.send', { channel, text })).message;
  }

  async chatSubscribe(channels: string[]): Promise<string[]> {
    return (await this.call('chat.subscribe', { channels })).subscribed;
  }

  async chatUnsubscribe(channels?: string[]): Promise<string[]> {
    return (await this.call('chat.unsubscribe', channels ? { channels } : {})).subscribed;
  }

  async chatMarkRead(channel: string, seq: number): Promise<{ readSeq: number; unread: number }> {
    const r = await this.call('chat.markRead', { channel, seq });
    return { readSeq: r.readSeq, unread: r.unread };
  }

  async getAuction(): Promise<AuctionInfo> {
    return this.call('auction.get', {});
  }

  buyAuction(expectedPrice: number): Promise<TypedCommandOutcome<AuctionBuyCommandResult>> {
    return this.typedCommand('auction.buy', { expectedPrice });
  }

  async getNews(limit?: number): Promise<NewsItem[]> {
    return (await this.call('news.list', limit === undefined ? {} : { limit })).items;
  }

  async saveReplay(replay: PayloadOf<'replay.save'>): Promise<string> {
    return (await this.call('replay.save', replay)).replayId;
  }

  async listReplays(playerId?: PlayerId, limit?: number): Promise<ReplaySummary[]> {
    const p: PayloadOf<'replay.list'> = {};
    if (playerId) p.playerId = playerId;
    if (limit) p.limit = limit;
    return (await this.call('replay.list', p)).items;
  }

  async getReplay(replayId: string): Promise<ReplayRecord> {
    return (await this.call('replay.get', { replayId })).replay;
  }

  async startBattle(init: BattleInit): Promise<SessionInfo> {
    return (await this.call('session.start', { init })).session;
  }

  finishBattle(
    sessionId: string,
    result: BattleResult,
    opts: { finalHash?: string; log?: CommandLogData; timeoutMs?: number } = {},
  ): Promise<ResultOf<'session.finish'>> {
    const p: PayloadOf<'session.finish'> = { sessionId, result };
    if (opts.finalHash) p.finalHash = opts.finalHash;
    if (opts.log) p.log = opts.log;
    return this.call('session.finish', p, { timeoutMs: opts.timeoutMs });
  }

  async forfeitBattle(sessionId: string): Promise<void> {
    await this.call('session.forfeit', { sessionId });
  }

  disputeBattle(sessionId: string, tick: number, reason?: string): Promise<ResultOf<'session.dispute'>> {
    const p: PayloadOf<'session.dispute'> = { sessionId, tick: Math.max(0, Math.floor(tick)) };
    if (reason) p.reason = reason.slice(0, 200);
    return this.call('session.dispute', p);
  }

  async findMatch(opts: { teams: number; wormsPerTeam: number }): Promise<MatchTicket> {
    return (await this.call('matchmaking.join', { mode: 'just', teams: opts.teams, wormsPerTeam: opts.wormsPerTeam })).ticket;
  }

  async cancelMatch(ticketId: string): Promise<void> {
    await this.call('matchmaking.leave', { ticketId });
  }

  /** The first page of the public lobby rooms (room.list pages further with `request` and the `next` cursor). */
  async listRooms(): Promise<Room[]> {
    const r = await this.call('room.list', {});
    return r.items ?? r.rooms;
  }

  async listRoomsPage(opts: { cursor?: string; limit?: number } = {}): Promise<{ rooms: Room[]; next: string | null }> {
    const p: PayloadOf<'room.list'> = {};
    if (opts.cursor !== undefined) p.cursor = opts.cursor;
    if (opts.limit !== undefined) p.limit = opts.limit;
    const r = await this.call('room.list', p);
    return { rooms: r.items ?? r.rooms, next: r.next ?? null };
  }

  async getRoom(roomId: string): Promise<Room> {
    return (await this.call('room.get', { roomId })).room;
  }

  async createRoom(opts: { settings?: RoomSettings; private?: boolean; maxPlayers?: number } = {}): Promise<Room> {
    return (await this.call('room.create', opts)).room;
  }

  async joinRoom(target: { roomId?: string; code?: string }): Promise<Room> {
    return (await this.call('room.join', target)).room;
  }

  async leaveRoom(roomId: string): Promise<void> {
    await this.call('room.leave', { roomId });
  }

  async setReady(roomId: string, ready: boolean): Promise<Room> {
    return (await this.call('room.setReady', { roomId, ready })).room;
  }

  async setTeam(roomId: string, team: number): Promise<Room> {
    return (await this.call('room.setTeam', { roomId, team })).room;
  }

  async updateRoomSettings(roomId: string, settings: RoomSettings): Promise<Room> {
    return (await this.call('room.update', { roomId, settings })).room;
  }

  async sendChat(roomId: string, text: string): Promise<void> {
    await this.call('room.chat', { roomId, text });
  }

  async startRoom(roomId: string): Promise<SessionInfo> {
    return (await this.call('room.start', { roomId })).session;
  }

  getSession(sessionId: string): Promise<ResultOf<'session.get'>> {
    return this.call('session.get', { sessionId });
  }

  createLockstep(session: SessionInfo, opts: { hasher?: StateHasher; flushEveryTicks?: number } = {}): LockstepCoordinator {
    if (session.turn !== 0) throw new Error('createLockstep: the session is already in progress; use resumeLockstep(sessionId)');
    const me = this.client.playerId;
    const localTeams = session.participants.find((p) => p.playerId === me)?.teams ?? [];
    return new LockstepCoordinator({
      localTeams,
      firstTeam: session.currentTeam,
      startTick: session.turnStartTick,
      link: createLockstepLink(this.client, session.sessionId),
      hasher: opts.hasher,
      flushEveryTicks: opts.flushEveryTicks,
    });
  }

  async resumeLockstep(sessionId: string, opts: { hasher?: StateHasher; flushEveryTicks?: number } = {}): Promise<LockstepCoordinator> {
    const { session, log } = await this.call('session.get', { sessionId });
    const me = this.client.playerId;
    return new LockstepCoordinator({
      localTeams: session.participants.find((p) => p.playerId === me)?.teams ?? [],
      firstTeam: log.turns[0].team,
      initialLog: log,
      link: createLockstepLink(this.client, sessionId),
      hasher: opts.hasher,
      flushEveryTicks: opts.flushEveryTicks,
    });
  }

  on<E extends EventType>(event: E, handler: (payload: EventMap[E]) => void): () => void {
    return this.client.on(event, handler);
  }

  onEventGap(listener: () => void): () => void {
    return this.client.onGap(() => listener());
  }

  get serverInfo(): HelloResult | null {
    return this.client.serverInfo;
  }

  async dispose(): Promise<void> {
    await this.client.close();
    this.offStatus();
    for (const off of this.offClanEvents) off();
  }
}

export type GameServicesConfig = BackendConnectionConfig & {
  /** Where the guest device token and the session's tokens are kept (default: localStorage when available). */
  credentials?: CredentialStore;
  clientVersion?: string;
  /** This installation as the backend lists it in the account's sessions (default: defaultDevice()). */
  device?: DeviceInfo;
  /**
   * Let `ensureSession` sign in as a guest when no session is stored (headless tools, tests, the server's battle
   * simulator). Default: false, except the test/dev page switch `?autoguest=1` with an in-process backend
   * (config.ts autoGuestRequested); never implied for a server.
   */
  guest?: boolean;
};

/** The device info of this client: platform 'web' in a browser, 'node' elsewhere (headless tools and tests). */
export function defaultDevice(): DeviceInfo {
  return { platform: typeof document !== 'undefined' ? 'web' : 'node' };
}

/**
 * Default credential store of a backend: localStorage (when available) under a key of its own per backend origin
 * (`fwp.backend.credentials:<origin>`; the offline backend keeps the historical key, so its guest and progress stay),
 * so a token issued by one server is never sent to another. The memory backend keeps nothing (it forgets its
 * accounts on reload, and must not replace the stored offline guest).
 */
export function defaultCredentialStore(config: BackendConnectionConfig, webStorage?: WebStorageLike | null): CredentialStore {
  if (config.transport === 'in-process' && config.storage !== undefined && config.storage !== 'localStorage') return new MemoryCredentialStore();
  let ls: WebStorageLike | null | undefined = webStorage;
  if (ls === undefined) {
    try {
      ls = (globalThis as { localStorage?: WebStorageLike }).localStorage;
    } catch {
      ls = null; /* no localStorage */
    }
  }
  return ls ? new WebStorageCredentialStore(ls, credentialsKey(backendOrigin(config))) : new MemoryCredentialStore();
}

/** Builds the transport described by the config. */
export async function createTransport(config: BackendConnectionConfig): Promise<Transport> {
  switch (config.transport) {
    case 'in-process': {
      const { transport: _t, ...opts } = config;
      const seed = battleSeedRequested(config);
      if (seed !== null) opts.coreOptions = { ...(opts.coreOptions ?? {}), fixedBattleSeed: seed };
      return new InProcessTransport(await createInProcessBackend(opts), true);
    }
    case 'http':
      // retries: 0 — BackendClient retries every request; a second layer would double the time a call can take
      // (requestBudgetMs, which the game's bridge watchdog must outlast).
      return new HttpTransport({ baseUrl: config.baseUrl, fetch: config.fetch, pollWaitMs: config.pollWaitMs, timeoutMs: config.timeoutMs, retries: 0 });
    case 'websocket':
      return new WebSocketTransport({ url: config.url, WebSocketImpl: config.WebSocketImpl, timeoutMs: config.timeoutMs });
  }
}

/** Creates the facade over a fresh transport and negotiates the protocol version. */
export async function createGameServices(config: GameServicesConfig): Promise<GameServices> {
  const transport = await createTransport(config);
  return createGameServicesForTransport(transport, {
    credentials: config.credentials ?? defaultCredentialStore(config),
    clientVersion: config.clientVersion,
    device: config.device,
    guest: config.guest ?? autoGuestRequested(config),
  });
}

/** `guest`: see GameServicesConfig.guest (default false). */
export async function createGameServicesForTransport(
  transport: Transport,
  opts: { credentials?: CredentialStore; clientVersion?: string; device?: DeviceInfo; guest?: boolean } = {},
): Promise<GameServices> {
  const client = new BackendClient({
    transport,
    credentials: opts.credentials ?? new MemoryCredentialStore(),
    clientVersion: opts.clientVersion,
    device: opts.device ?? defaultDevice(),
  });
  const services = new GameServicesImpl(client, transport.kind, opts.guest === true);
  try {
    await transport.connect();
    await client.hello();
  } catch (err) {
    // A failed start must not leave a transport reconnecting in the background (the caller retries with a new one).
    await services.dispose().catch(() => undefined);
    throw err;
  }
  return services;
}

export type { BattleMode };
